// Hook con TODO el manejo de adjuntos del modal de correo: subida normal,
// oversize (evidencia + ZIP + shortlink), imagen inline-vs-adjunto, drag &
// drop a nivel modal y la lista de chips. Extraído de EnviarCorreoModal
// (modularización 2026-06-11) sin cambiar comportamiento.
import { useMemo, useState } from "react";
import {
  ApiError,
  api,
  type CorreoAdjuntoOut,
  type CorreoOversizeOut,
  type EvidenciaOut,
} from "@/lib/api";
import type { RichTextEditorHandle } from "@/components/RichTextEditor";
import { useToast } from "@/ui/Toast";
import {
  MAX_ADJUNTO_BYTES,
  MAX_TOTAL_ADJUNTOS_BYTES,
  fmtMB,
} from "@/components/correo/utilsCorreo";

export function useAdjuntosCorreo({
  casoId,
  editorActivo,
}: {
  casoId: number;
  editorActivo: () => RichTextEditorHandle | null;
}) {
  const toast = useToast();
  const [adjuntos, setAdjuntos] = useState<CorreoAdjuntoOut[]>([]);
  const [subiendoAdjuntos, setSubiendoAdjuntos] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Pregunta inline-vs-attach cuando el user suelta una imagen <=10MB.
  // Joseph eligio "preguntar al soltar" en el plan G3 (no auto-decide).
  const [askImagen, setAskImagen] = useState<File | null>(null);
  // Pregunta de confirmacion cuando el archivo pasa los 10MB — el
  // backend lo procesa via /adjuntos/oversize (evidencia + paquete +
  // shortlink), sin salir del modal.
  const [askOversize, setAskOversize] = useState<File | null>(null);
  // Imagen que viene de una evidencia (no un File): misma pregunta inline-vs-adjunta.
  const [askImagenEvidencia, setAskImagenEvidencia] = useState<EvidenciaOut | null>(null);

  const totalAdjuntosBytes = useMemo(
    () => adjuntos.reduce((s, a) => s + a.bytes, 0),
    [adjuntos],
  );

  async function subirAdjuntoNormal(file: File) {
    if (totalAdjuntosBytes + file.size > MAX_TOTAL_ADJUNTOS_BYTES) {
      toast.error(
        `Excede el total (${fmtMB(MAX_TOTAL_ADJUNTOS_BYTES)} MB)`,
        "Quita algún adjunto o sube este como archivo grande (enlace seguro de descarga).",
      );
      return;
    }
    setSubiendoAdjuntos(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const adj = await api.postForm<CorreoAdjuntoOut>(
        `/api/casos/${casoId}/correos/adjuntos`,
        fd,
      );
      setAdjuntos((prev) => [...prev, adj]);
      toast.success("Adjunto agregado", adj.nombre);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error desconocido";
      toast.error("No se pudo adjuntar", msg);
    } finally {
      setSubiendoAdjuntos(false);
    }
  }

  async function subirOversize(file: File) {
    setSubiendoAdjuntos(true);
    setAskOversize(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const result = await api.postForm<CorreoOversizeOut>(
        `/api/casos/${casoId}/correos/adjuntos/oversize`,
        fd,
      );
      // Insertar como bloque chip en el editor: link + password + tam.
      // Va al cuerpo del correo (cliente o proveedor segun modo).
      const mb = fmtMB(file.size);
      const editor = editorActivo();
      if (!editor) {
        toast.error("No hay editor activo para insertar el link");
        return;
      }
      // HTML inline-friendly: una linea visible en el correo y un caja
      // chica con la password para que el cliente la copie.
      // Escapado: el nombre del archivo viene del usuario y podria traer
      // HTML (audit femail-2).
      const esc = (s: string) =>
        s
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const html =
        `<p>` +
        `<strong>Archivo adjunto:</strong> ` +
        `<a href="${esc(result.short_url)}" target="_blank" rel="noopener">${esc(file.name)}</a> ` +
        `(${mb} MB)<br>` +
        `<small>Contrasena del ZIP: <code>${esc(result.password)}</code></small>` +
        `</p>`;
      editor.insertHtml(html);
      toast.success(
        "Link insertado en el correo",
        `Archivo grande subido como ZIP cifrado (${mb} MB). El destinatario lo descarga del link.`,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error desconocido";
      toast.error("No se pudo procesar el archivo", msg);
    } finally {
      setSubiendoAdjuntos(false);
    }
  }

  function insertarImagenInline(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const editor = editorActivo();
      if (editor) {
        editor.insertImage(dataUrl, file.name);
        toast.success("Imagen insertada en el cuerpo");
      }
    };
    reader.onerror = () => toast.error("No se pudo leer la imagen");
    reader.readAsDataURL(file);
    setAskImagen(null);
  }

  // Adjuntar una evidencia existente del caso (copia server-side, sin ZIP).
  async function adjuntarEvidencia(ev: EvidenciaOut) {
    if (totalAdjuntosBytes + ev.size_bytes > MAX_TOTAL_ADJUNTOS_BYTES) {
      toast.error(
        `Excede el total (${fmtMB(MAX_TOTAL_ADJUNTOS_BYTES)} MB)`,
        "Quita algún adjunto o mándalo por ZIP (archivo grande).",
      );
      setAskImagenEvidencia(null);
      return;
    }
    setSubiendoAdjuntos(true);
    try {
      const adj = await api.post<CorreoAdjuntoOut>(
        `/api/casos/${casoId}/correos/adjuntos/desde-evidencia`,
        { evidencia_id: ev.id },
      );
      setAdjuntos((prev) => [...prev, adj]);
      toast.success("Adjunto agregado", adj.nombre);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error desconocido";
      toast.error("No se pudo adjuntar", msg);
    } finally {
      setSubiendoAdjuntos(false);
      setAskImagenEvidencia(null);
    }
  }

  // Insertar una evidencia de imagen inline en el cuerpo (baja el archivo del
  // servidor y lo incrusta como dataURL).
  async function insertarEvidenciaInline(ev: EvidenciaOut) {
    try {
      const res = await fetch(`/api/evidencias/${ev.id}/file`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("fetch falló");
      const blob = await res.blob();
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      editorActivo()?.insertImage(dataUrl, ev.nombre_original);
      toast.success("Imagen insertada en el cuerpo");
    } catch {
      toast.error("No se pudo insertar la imagen");
    } finally {
      setAskImagenEvidencia(null);
    }
  }

  function clasificarYProcesar(file: File) {
    // 1) Pasa el limite SMTP -> oversize (evidencia + zip + shortlink).
    if (file.size > MAX_ADJUNTO_BYTES) {
      setAskOversize(file);
      return;
    }
    // 2) Imagen -> preguntar inline vs attach.
    if (file.type.startsWith("image/")) {
      setAskImagen(file);
      return;
    }
    // 3) Resto -> attachment directo.
    void subirAdjuntoNormal(file);
  }

  function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    // Solo procesamos el primero si requiere pregunta — los demas se
    // ignoran para no apilar dialogos. El user puede arrastrar uno por
    // uno o usar el file picker que permite seleccionar varios chicos.
    const first = arr[0];
    if (first.size > MAX_ADJUNTO_BYTES || first.type.startsWith("image/")) {
      clasificarYProcesar(first);
      if (arr.length > 1) {
        toast.info(
          "Solo procese el primer archivo",
          "Arrastra uno a la vez cuando son imagenes o pesados (requieren elegir).",
        );
      }
      return;
    }
    // Lote de archivos chicos no-imagen: subir todos.
    for (const f of arr) {
      void subirAdjuntoNormal(f);
    }
  }

  async function removerAdjunto(id: number) {
    try {
      await api.del(`/api/casos/${casoId}/correos/adjuntos/${id}`);
      setAdjuntos((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error desconocido";
      toast.error("No se pudo quitar el adjunto", msg);
    }
  }

  // Drag handlers a nivel modal: si el drop ocurrio dentro del editor
  // TipTap (.ProseMirror), el editor ya lo desactivo (disableImageDrop)
  // — entonces caemos aqui igual. Eso es lo que queremos: cualquier
  // drop dentro del modal pasa por la pregunta inline-vs-attach.
  function onModalDragOver(e: React.DragEvent) {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  }
  function onModalDragLeave(e: React.DragEvent) {
    // Solo apagar el overlay cuando el cursor sale del wrapper raiz.
    // Usamos relatedTarget: si va a un descendiente, no apagamos.
    const rel = e.relatedTarget as Node | null;
    if (rel && e.currentTarget.contains(rel)) return;
    setDragOver(false);
  }
  function onModalDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) handleFiles(files);
  }

  return {
    adjuntos,
    subiendoAdjuntos,
    totalAdjuntosBytes,
    dragOver,
    askImagen,
    setAskImagen,
    askOversize,
    setAskOversize,
    subirAdjuntoNormal,
    subirOversize,
    insertarImagenInline,
    askImagenEvidencia,
    setAskImagenEvidencia,
    adjuntarEvidencia,
    insertarEvidenciaInline,
    handleFiles,
    removerAdjunto,
    onModalDragOver,
    onModalDragLeave,
    onModalDrop,
  };
}
